import type { GroupId } from "@repo/app-types/entities/group";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { createContext, type FC, type ReactNode, useContext } from "react";
import {
  InteractiveReferenceBoundary,
  useIsInsideInteractiveReference,
} from "./interactiveReferenceBoundary.js";

export interface TeammateGroupReference {
  groupId: GroupId;
  /**
   * The playbook whose teammates page the reference links into, when the
   * content or its surface knows one. The host picks a fallback otherwise.
   */
  playbookId?: PlaybookId;
}

export interface TeammateGroupReferenceWrapperProps {
  reference: TeammateGroupReference;
  /** Base styling shared by the linked and inert reference. */
  className: string;
  children: ReactNode;
}

/**
 * An app-supplied component that turns a teammate group reference into a link
 * to the teammates page filtered to the group. Apps that don't supply one
 * render references as styled text.
 */
export type TeammateGroupReferenceWrapper =
  FC<TeammateGroupReferenceWrapperProps>;

const TeammateGroupReferenceContext = createContext<
  TeammateGroupReferenceWrapper | undefined
>(undefined);

export const TeammateGroupReferenceProvider =
  TeammateGroupReferenceContext.Provider;

/**
 * Renders a teammate group reference from the AST: through the host wrapper
 * from `TeammateGroupReferenceProvider` when one is supplied, otherwise as a
 * styled span. Inside another interactive reference it renders as plain
 * content, and references inside it render plain too.
 */
export const TeammateGroupReferenceNode: FC<
  TeammateGroupReferenceWrapperProps
> = (props) => {
  const { reference, className, children } = props;
  const Wrapper = useContext(TeammateGroupReferenceContext);
  const isInsideInteractiveReference = useIsInsideInteractiveReference();
  if (isInsideInteractiveReference) {
    return <>{children}</>;
  }
  if (!Wrapper) {
    return <span className={className}>{children}</span>;
  }
  return (
    <Wrapper reference={reference} className={className}>
      <InteractiveReferenceBoundary>{children}</InteractiveReferenceBoundary>
    </Wrapper>
  );
};
